// Sticky summary card on the right of the wizard. Reflects the current draft.

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typo, Icon, theme } from '../../../../ui';
import { CHANNEL, OFFER } from '../../constants';

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 20px;
  /* Sticky only when the aside lives in the right rail. Below laptop the wizard
     layout stacks and sticky would pin this to the scroll top, which looks broken. */
  @media (min-width: 1200px) {
    position: sticky;
    top: 24px;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.NEU_100};
  font-size: 13px;

  &:last-child { border-bottom: 0; }
`;

const Key = styled.span`
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Value = styled.span`
  font-weight: 600;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
`;

// Tip card mirrors the prototype: neutral white card, orange lightbulb glyph,
// quiet body text. No coloured background — the icon does the affordance work.
const TipCard = styled(Card)`
  position: static;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
`;

const TipBody = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const channelsLabel = (draft) => {
  const channels = draft?.channels ?? [];
  const labels = channels.map((c) => (c === CHANNEL.EMAIL ? 'Email' : 'In-app'));
  return labels.join(' + ') || '—';
};

const offerLabel = (draft) => {
  if (draft?.offer === OFFER.DISCOUNT && draft?.discount?.percentage) return `Discount ${draft.discount.percentage}%`;
  if (draft?.offer === OFFER.INVITATION) return 'Invitation';
  if (draft?.offer === OFFER.NONE) return 'None';
  return '—';
};

const audienceLabel = (draft) => {
  const reach = draft?.audience?.estimatedReach;
  return reach != null ? reach.toLocaleString() : '—';
};

const inAppWindow = (draft) => {
  const from = draft?.schedule?.networkUpsell?.activeFrom;
  const to = draft?.schedule?.networkUpsell?.activeUntil;
  if (!from || !to) return '—';
  try {
    const f = new Date(from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const t = new Date(to).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f} → ${t}`;
  } catch { return '—'; }
};

const WizardSummaryAside = ({ draft }) => {
  const { t } = useTranslation();
  const channels = draft?.channels ?? [];
  return (
    <Aside>
      <Card>
        <Typo.TextTitle style={{ marginBottom: 12 }}>{t('campaigns.summary', 'Summary')}</Typo.TextTitle>
        <Row><Key>{t('campaigns.summary.name', 'Name')}</Key><Value>{draft?.name || '—'}</Value></Row>
        <Row><Key>{t('campaigns.summary.channels', 'Channels')}</Key><Value>{channelsLabel(draft)}</Value></Row>
        <Row><Key>{t('campaigns.summary.offer', 'Offer')}</Key><Value>{offerLabel(draft)}</Value></Row>
        <Row><Key>{t('campaigns.summary.audience', 'Audience')}</Key><Value>{audienceLabel(draft)}</Value></Row>
        {channels.includes(CHANNEL.EMAIL) && (
          <Row><Key>{t('campaigns.summary.emailSend', 'Email send')}</Key><Value>{formatDate(draft?.schedule?.email?.sendAt)}</Value></Row>
        )}
        {channels.includes(CHANNEL.NETWORK_UPSELL) && (
          <Row><Key>{t('campaigns.summary.inAppWindow', 'In-app window')}</Key><Value>{inAppWindow(draft)}</Value></Row>
        )}
      </Card>
      <TipCard>
        <TipHeader>
          <Icon.Lightbulb size='small' color={theme.colors.SECONDARY} />
          {t('campaigns.tip', 'Tip')}
        </TipHeader>
        <TipBody>
          {t(
            'campaigns.tip.tuesdayMornings',
            'Emails sent on Tuesday mornings have the highest open rate for your store historically (27.4%).',
          )}
        </TipBody>
      </TipCard>
    </Aside>
  );
};

export default WizardSummaryAside;
